import { useMemo } from 'react';

import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';
import type { LabelProps } from 'recharts';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ChartConfig, ChartContainer } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { AnalyticsEmptyState } from '@/modules/analytics/components/aiAgents/AnalyticsEmptyState';
import { formatDurationFromSeconds } from '@/modules/analytics/utils/formatDuration';
import { CampaignCallDurationBucket } from '@/shared/types/campaigns';

import { fitsInsideBar, formatBucketPercentage, formatTotalDuration } from './callDurationLabel';
import { splitVoicemail } from './callDurationSplit';

// Keyed off `bucket_order` rather than the server label, so the buckets translate.
const BUCKET_LABEL_KEYS: Record<number, string> = {
  1: 'campaigns.performance.charts.call_duration_under_30s',
  2: 'campaigns.performance.charts.call_duration_30_60s',
  3: 'campaigns.performance.charts.call_duration_1_2m',
  4: 'campaigns.performance.charts.call_duration_over_2m',
};

const BUCKET_COLORS: Record<number, string> = {
  1: 'var(--chart-2)',
  2: 'var(--chart-4)',
  3: 'var(--chart-3)',
  4: 'var(--chart-1)',
};

// Per-bar colours come from <Cell>, so the config only has to register the series.
const chartConfig = { percentage: {} } satisfies ChartConfig;

// White inside the bar, or in foreground text just past its end when the bar is
// narrower than the label — white on the card background reads as a missing number.
const BarValueLabel = ({ x, y, width, height, value }: LabelProps) => {
  const barX = Number(x ?? 0);
  const barWidth = Number(width ?? 0);
  const inside = fitsInsideBar(barWidth);

  return (
    <text
      className={inside ? 'fill-white' : 'fill-foreground'}
      dominantBaseline="central"
      fontSize={12}
      textAnchor={inside ? 'end' : 'start'}
      x={inside ? barX + barWidth - 8 : barX + barWidth + 6}
      y={Number(y ?? 0) + Number(height ?? 0) / 2}
    >
      {formatBucketPercentage(Number(value ?? 0))}
    </text>
  );
};

interface Props {
  buckets?: CampaignCallDurationBucket[];
  isLoading?: boolean;
  isError?: boolean;
}

export const CallDurationChart = ({ buckets, isLoading, isError }: Props) => {
  const { t } = useTranslation();

  const split = useMemo(() => splitVoicemail(buckets ?? []), [buckets]);

  const series = useMemo(
    () =>
      split.buckets.map(({ bucketOrder, label, percentage }) => {
        const key = BUCKET_LABEL_KEYS[bucketOrder];

        return { bucketOrder, label: key ? t(key) : label, percentage };
      }),
    [split, t]
  );

  // The axis is hidden and every bar is labelled, so the plot scales to the data
  // instead of a fixed 0-100 span that would squash the short buckets.
  const domainMax = useMemo(
    () => Math.max(...series.map(({ percentage }) => percentage), 0) * 1.2 || 1,
    [series]
  );

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }

  // A rejected query returns no rows, which is not the same as a campaign with no
  // answered calls: the function raises rather than answering with zeros.
  const hasData = !isError && series.length > 0 && split.humanTotal > 0;

  return (
    <div
      className="flex flex-col rounded-xl border border-stroke bg-background p-6"
      data-testid="call-duration-chart"
    >
      <h3 className="text-sm leading-5 font-semibold text-foreground">
        {t('campaigns.performance.charts.call_duration')}
      </h3>
      <p className="mt-1 mb-4 text-xs leading-4 text-foreground-muted">
        {t('campaigns.performance.charts.call_duration_subtitle')}
      </p>
      {hasData ? (
        <dl className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <dt className="flex items-center gap-1 text-xs leading-4 text-foreground-muted">
              {t('campaigns.performance.charts.conversation_time')}
              <InfoTooltip
                content={t('campaigns.performance.charts.conversation_time_tooltip')}
                size="large"
                side="top"
              >
                <button
                  type="button"
                  aria-label={t('common.info')}
                  className="rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <InfoIcon className="size-3.5 shrink-0 text-foreground-muted" />
                </button>
              </InfoTooltip>
            </dt>
            <dd className="mt-1 text-lg leading-6 font-semibold text-foreground">
              {formatTotalDuration(split.conversationSeconds)}
            </dd>
            {split.voicemailSeconds > 0 && (
              <dd className="mt-0.5 text-xs leading-4 text-foreground-muted">
                {t('campaigns.performance.charts.voicemail_time', {
                  0: formatTotalDuration(split.voicemailSeconds),
                })}
              </dd>
            )}
          </div>
          <div>
            <dt className="text-xs leading-4 text-foreground-muted">
              {t('campaigns.performance.charts.avg_per_call')}
            </dt>
            <dd className="mt-1 text-lg leading-6 font-semibold text-foreground">
              {formatDurationFromSeconds(split.averageSeconds)}
            </dd>
          </div>
        </dl>
      ) : null}
      {hasData ? (
        <ChartContainer className="aspect-auto h-48 w-full [&>div]:h-full" config={chartConfig}>
          <BarChart
            barCategoryGap="25%"
            data={series}
            layout="vertical"
            margin={{ top: 0, right: 40, bottom: 0, left: 0 }}
          >
            <XAxis domain={[0, domainMax]} hide type="number" />
            <YAxis
              axisLine={false}
              dataKey="label"
              tickLine={false}
              tickMargin={4}
              type="category"
              width={64}
            />
            <Bar dataKey="percentage" maxBarSize={28} radius={4}>
              {series.map(({ bucketOrder }) => (
                <Cell key={bucketOrder} fill={BUCKET_COLORS[bucketOrder]} />
              ))}
              <LabelList content={<BarValueLabel />} dataKey="percentage" />
            </Bar>
          </BarChart>
        </ChartContainer>
      ) : (
        <div className="flex h-48 w-full items-center justify-center">
          <AnalyticsEmptyState
            description={
              isError
                ? t('analytics.reports_detail_metric_query_error')
                : t('campaigns.performance.charts.no_call_durations')
            }
            title={isError ? t('common.something_wrong') : t('analytics.no_data_available')}
          />
        </div>
      )}
      {!hasData && !isError && split.voicemailSeconds > 0 && (
        <p className="mt-3 text-xs leading-4 text-foreground-muted">
          {t('campaigns.performance.charts.voicemail_only_time', {
            0: formatTotalDuration(split.voicemailSeconds),
          })}
        </p>
      )}
    </div>
  );
};
